import React, { useRef, useState, useEffect } from 'react';
import { Tooltip, Typography, Box, TypographyProps } from '@mui/material';

interface TooltipCellProps extends TypographyProps {
  value: string;
  maxWidth?: string | number;
  children?: React.ReactNode;
}

const TooltipCell: React.FC<TooltipCellProps> = ({
  value,
  maxWidth = '100%',
  children,
  sx,
  ...typographyProps
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [value]);

  const content = children || (
    <Typography
      ref={textRef}
      sx={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth,
        ...sx,
      }}
      {...typographyProps}
    >
      {value}
    </Typography>
  );

  if (isOverflowing) {
    return (
      <Tooltip title={value} placement="top" arrow>
        <Box sx={{ maxWidth, overflow: 'hidden' }}>
          {content}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box sx={{ maxWidth, overflow: 'hidden' }}>
      {content}
    </Box>
  );
};

export default TooltipCell; 